/**
 * The client-held cursor stack that makes a Previous button possible.
 *
 * `PageInfoSchema` says it plainly: *"Opaque. Echo `next_cursor` back verbatim;
 * never construct one."* There is no previous cursor on the wire and no
 * arithmetic that could produce one — offset pagination is not offered at all,
 * because scope is a query predicate (NFR-06) and an offset into a row set that
 * differs per caller and shifts as contracts are added is a footgun with no
 * upside at ten contracts a month.
 *
 * So Previous is a **pop**: the cursors that produced the pages already
 * displayed, kept in order. `cursors[i]` is the cursor that fetched page
 * `i + 2`; page one has no cursor, so the stack is empty there.
 *
 * These are plain functions rather than methods so a surface can hold the value
 * in `useState` and React can see it change.
 */
export interface CursorStack {
  readonly cursors: readonly string[];
}

export const EMPTY_CURSOR_STACK: CursorStack = { cursors: [] };

/** Push the cursor that fetched the page now being displayed. */
export function pushCursor(stack: CursorStack, cursor: string): CursorStack {
  return { cursors: [...stack.cursors, cursor] };
}

/**
 * Pop back one page. Returns the cursor to re-fetch with — `undefined` for the
 * first page, which is requested with no cursor at all.
 */
export function popCursor(stack: CursorStack): {
  readonly stack: CursorStack;
  readonly cursor: string | undefined;
} {
  const cursors = stack.cursors.slice(0, -1);
  return { stack: { cursors }, cursor: cursors[cursors.length - 1] };
}

/** 1-based page number for the pager's readout. */
export function pageNumber(stack: CursorStack): number {
  return stack.cursors.length + 1;
}
